import { isBoolean } from "@sinch/feature-flags";
import { useFeatureFlag } from "@sinch/feature-flags/react";
import { type FC, useState } from "react";
import styled, { css } from "styled-components";
import { useUser } from "../../api/hooks/useUser";
import { type MmPaymentType, MM_PAYMENT_TYPE } from "../../api/mm/mm.types";
import { authStrategy } from "../../auth";
import { MFELoaderWrapper } from "../../components/MFELoaderWrapper";
import { useLocation } from "../../components/Router/state";
import { matchPath } from "../../components/Router/utils";
import { TopBanner } from "../../components/TopBanner/TopBanner";
import {
  useVendorConfigHandler,
  useMenuConfigHandler,
  usePartnerIdHandler,
  useChangeAccountHandler,
  useLoadMfeHandler,
  useLogoutHandler,
} from "../../handlers";
import { safeRetrieveValue } from "../../helpers/safeRetrieveValue";
import { useSidebarState, useSidebarBusEventHandler } from "../../hooks/state";
import { useInboxConfig } from "../../hooks/useInboxConfig";
import { useNavigationConfigTransformer } from "../../navigationConfig";
import { MaxBreakPoints } from "../../style/constants";
import { LoadingScreenContainer } from "./components/LoadingScreen";
import { SidebarScaffold } from "./components/Sidebar";
import { SIDEBAR, TRANSITION_SPEED } from "./components/constants";
import { useUiStateHandler } from "./handlers/uiStateHandler";
import { useExceptionMonitoringUser } from "./hooks/useSetExceptionMonitoringUser";
import { useUpdateFeatureFlags } from "./hooks/useUpdateFeatureFlags";
import {
  mmNavigationConfig,
  nonMenuHubContainerPaths,
} from "./navigationConfig";
import { useInitZendesk } from "./zendeskWidget";
import "@nectary/components/spinner";

const navWidth = (isCollapsed: boolean) =>
  isCollapsed ? SIDEBAR.collapsedWidth : SIDEBAR.width;

const GridContainer = styled.div<{ isCollapsed: boolean }>`
  display: grid;
  transition: grid-template-columns ${TRANSITION_SPEED}s;
  grid-template-rows: 75px 1fr;
  grid-template-areas: "nav nav" "main main";
  height: 100%;
  position: relative;

  @media screen and (min-width: ${MaxBreakPoints.MEDIUM}px) {
    grid-template-rows: none;
    ${({ isCollapsed }) =>
      css`
        grid-template-columns: ${navWidth(isCollapsed)}px calc(
            100% - ${navWidth(isCollapsed)}px
          );
      `}
    grid-template-areas: "nav main";
    flex: 1;
  }
`;

const GridItem = styled.div<{ area: "main" | "nav" }>`
  grid-area: ${({ area }) => area};
`;

export const ShellWrapper: FC = ({ children }) => {
  const oneSaasMenu = useFeatureFlag("oneSaasMenu", isBoolean, false);
  const [menuMfeStatus, setMenuMfeStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [user] = useUser();

  useExceptionMonitoringUser(user);

  const userSettings = user?.settings || {};

  const inboxConfig = useInboxConfig();
  const navigationConfig = useNavigationConfigTransformer(
    mmNavigationConfig,
    user,
    {
      paymentType: safeRetrieveValue(
        userSettings,
        "paymentType",
        (x): x is MmPaymentType => MM_PAYMENT_TYPE.safeParse(x).success
      ),
      inboxVersion: inboxConfig.inboxVersion,
    }
  );
  useInitZendesk();
  useVendorConfigHandler();
  useMenuConfigHandler(navigationConfig);
  usePartnerIdHandler();
  useUiStateHandler();
  useChangeAccountHandler();
  useLoadMfeHandler();
  useLogoutHandler(authStrategy.logout);
  const updatedFeatureFlag = useUpdateFeatureFlags(user);

  const state = useSidebarState();
  const location = useLocation();

  const isNonMenuPath = nonMenuHubContainerPaths.some((path) =>
    matchPath(path, location.pathname)
  );

  useSidebarBusEventHandler();

  if (!user || !updatedFeatureFlag) {
    return <SidebarScaffold skeleton={true} />;
  }

  return oneSaasMenu && !isNonMenuPath ? (
    <GridContainer isCollapsed={state.isCollapsed}>
      <SidebarScaffold skeleton={menuMfeStatus === "loading"} />
      <GridItem area="nav">
        <MFELoaderWrapper
          name="Menu"
          module="MainEntry"
          basePath="/"
          style={{ height: "100%", width: "100%" }}
          Fallback={() => (
            <LoadingScreenContainer>
              <sinch-spinner size="l" />
            </LoadingScreenContainer>
          )}
          onMount={() => {
            setMenuMfeStatus("ready");
          }}
        />
      </GridItem>

      <GridItem area="main">
        <TopBanner />
        <div>
          <MFELoaderWrapper
            name="container"
            module="quickSend"
            basePath="/"
            Fallback={() => (
              <LoadingScreenContainer>
                <sinch-spinner size="l" />
              </LoadingScreenContainer>
            )}
          />
          {children}
        </div>
      </GridItem>
    </GridContainer>
  ) : (
    <>{children}</>
  );
};
